import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, X } from 'lucide-react';

const CoursesForm = ({ data, onSave }) => {
  // Convert flat array data to year-grouped format
  const convertToYearFormat = (inputData) => {
    const defaultFormat = { '9': [], '10': [], '11': [], '12': [] };
    
    if (!inputData) return defaultFormat;
    
    // If it's already in the correct format (object with year keys)
    if (typeof inputData === 'object' && !Array.isArray(inputData)) {
      return { ...defaultFormat, ...inputData };
    }
    
    // If it's a flat array, group by year
    if (Array.isArray(inputData)) {
      inputData.forEach(course => {
        const year = String(course.year || '9');
        if (defaultFormat[year]) {
          defaultFormat[year].push(course);
        }
      });
      return defaultFormat;
    }
    
    return defaultFormat;
  };

  const [coursesByYear, setCoursesByYear] = useState(convertToYearFormat(data));
  const [activeYear, setActiveYear] = useState('9');

  // Update state when data prop changes
  useEffect(() => {
    setCoursesByYear(convertToYearFormat(data));
  }, [data]);

  const subjects = ['English', 'Mathematics', 'Science', 'Social Studies', 'Foreign Language', 'Arts', 'Computer Science', 'Physical Education', 'Other'];
  const levels = ['Regular', 'Honors', 'AP', 'IB', 'College'];
  const grades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D', 'F', 'P', 'F'];
  const semesters = ['Fall', 'Spring', 'Full Year'];

  const addCourse = (year) => {
    const updated = { ...coursesByYear };
    updated[year] = [...updated[year], {
      course_name: '',
      subject_area: 'English',
      level: 'Regular',
      grade: 'A',
      credits: '1.0',
      semester: 'Full Year'
    }];
    setCoursesByYear(updated);
  };

  const removeCourse = (year, index) => {
    const updated = { ...coursesByYear };
    updated[year] = updated[year].filter((_, i) => i !== index);
    setCoursesByYear(updated);
  };

  const handleChange = (year, index, field, value) => {
    const updated = { ...coursesByYear };
    updated[year][index][field] = value;
    setCoursesByYear(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Flatten courses for saving
    const allCourses = [];
    Object.entries(coursesByYear).forEach(([year, courses]) => {
      courses.forEach(course => {
        allCourses.push({ ...course, year: parseInt(year) });
      });
    });
    onSave(allCourses);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Self-Reported Academic Record (SRAR):</strong> List all courses taken in grades 9-12 with grades.
        </p>
      </div>

      <Tabs value={activeYear} onValueChange={setActiveYear}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="9">Grade 9</TabsTrigger>
          <TabsTrigger value="10">Grade 10</TabsTrigger>
          <TabsTrigger value="11">Grade 11</TabsTrigger>
          <TabsTrigger value="12">Grade 12</TabsTrigger>
        </TabsList>

        {['9', '10', '11', '12'].map(year => (
          <TabsContent key={year} value={year} className="space-y-4">
            {coursesByYear[year].map((course, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Course {index + 1}</h4>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCourse(year, index)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Course Name *</Label>
                    <Input value={course.course_name} onChange={(e) => handleChange(year, index, 'course_name', e.target.value)} required />
                  </div>
                  <div>
                    <Label>Subject *</Label>
                    <Select value={course.subject_area} onValueChange={(value) => handleChange(year, index, 'subject_area', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Level *</Label>
                    <Select value={course.level} onValueChange={(value) => handleChange(year, index, 'level', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {levels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Grade *</Label>
                    <Select value={course.grade} onValueChange={(value) => handleChange(year, index, 'grade', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Credits *</Label>
                    <Input type="number" step="0.5" value={course.credits} onChange={(e) => handleChange(year, index, 'credits', e.target.value)} required />
                  </div>
                  <div>
                    <Label>Semester *</Label>
                    <Select value={course.semester} onValueChange={(value) => handleChange(year, index, 'semester', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {semesters.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>
            ))}
            <Button type="button" variant="outline" onClick={() => addCourse(year)} className="w-full">
              <Plus className="h-4 w-4 mr-2" />Add Course to Grade {year}
            </Button>
          </TabsContent>
        ))}
      </Tabs>

      <div className="flex justify-end">
        <Button type="submit">Save & Continue</Button>
      </div>
    </form>
  );
};

export default CoursesForm;